"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { IconArrowNarrowLeft, IconArrowNarrowRight } from "@tabler/icons-react";
import type { LightboxImage } from "@/components/ui/image-lightbox";

type ImageGalleryCarouselProps = {
  images: LightboxImage[];
  title: string;
  onImageClick: (index: number) => void;
  previousLabel: string;
  nextLabel: string;
  priorityFirstImage?: boolean;
};

export const ImageGalleryCarousel = ({
  images,
  title,
  onImageClick,
  previousLabel,
  nextLabel,
  priorityFirstImage = false,
}: ImageGalleryCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const getScrollStep = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return 360;
    const card = container.querySelector<HTMLElement>("[data-gallery-card]");
    if (!card) return 360;
    return card.offsetWidth + 24;
  }, []);

  const checkScrollability = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  const handleScrollLeft = () => {
    const container = scrollRef.current;
    if (!container) return;
    const newScroll = Math.max(container.scrollLeft - getScrollStep(), 0);
    container.scrollTo({ left: newScroll, behavior: "smooth" });
  };

  const handleScrollRight = () => {
    const container = scrollRef.current;
    if (!container) return;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const newScroll = Math.min(container.scrollLeft + getScrollStep(), maxScroll);
    container.scrollTo({ left: newScroll, behavior: "smooth" });
  };

  useEffect(() => {
    checkScrollability();
    window.addEventListener("resize", checkScrollability);
    return () => {
      window.removeEventListener("resize", checkScrollability);
    };
  }, [images.length, checkScrollability]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleScrollLeft}
        disabled={!canScrollLeft}
        aria-label={previousLabel}
        className="absolute left-0 top-1/2 z-10 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-lg transition-all hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        <IconArrowNarrowLeft className="h-5 w-5 text-gray-700" />
      </button>

      <button
        type="button"
        onClick={handleScrollRight}
        disabled={!canScrollRight}
        aria-label={nextLabel}
        className="absolute right-0 top-1/2 z-10 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-lg transition-all hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        <IconArrowNarrowRight className="h-5 w-5 text-gray-700" />
      </button>

      <div
        ref={scrollRef}
        onScroll={checkScrollability}
        className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 [-webkit-overflow-scrolling:touch]"
      >
        {images.map((image, idx) => {
          const caption = image.title || `${title} ${idx + 1}`;

          return (
            <figure
              key={`${title}-${image.src}-${idx}`}
              data-gallery-card
              className="surface-card-soft min-w-[85%] cursor-pointer overflow-hidden transition-transform hover:scale-[1.02] sm:min-w-[70%] lg:min-w-[60%]"
              onClick={() => onImageClick(idx)}
            >
              <div className="relative aspect-[16/9] w-full">
                <Image
                  src={image.src}
                  alt={image.alt || caption}
                  title={caption}
                  fill
                  className="object-cover"
                  sizes="(min-width: 1024px) 60vw, 90vw"
                  priority={priorityFirstImage && idx === 0}
                />
              </div>
              <figcaption className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                {caption}
              </figcaption>
            </figure>
          );
        })}
      </div>
    </div>
  );
};
